import { match } from 'ts-pattern'
import type {
  Confidence,
  FindingAggregateResponse,
  FindingAggregateResponseStatistics,
  FindingResponse,
  FindingResultResponse,
  ScaClassificationResponse,
  Severity,
  SeverityUpdateFilter,
  TriageSuggestedStatusResponse,
} from '../src/utils/api-client/api-schemas'
import { SCAN_FINDINGS_PARAMS } from '../src/utils/api-client/query-params'

// ---------------------------------------------------------------------------
// MockFinding — the representative results of one finding, as the findings API models them
//
// Each analysis stage has one representative result, whose outcome type is the single source of
// that stage's state: a fix is completed, failed, blocked, excluded or without recommendations,
// never two of those at once. Modelling each stage as a discriminated union keeps contradictory
// fixtures — a completed fix that is also blocked, say — unrepresentable, so filtered rows and
// aggregate statistics cannot disagree. Everything else the API reports (analyzed, completed and
// in-progress analysis, the composite counts) is derived from these three below, the way
// `QueriesImpl` and `FindingSpecification` in pixee-platform derive it.
// ---------------------------------------------------------------------------

/** The representative triage result. A result without an outcome is still running. */
export type MockTriage =
  | {
      status: 'completed'
      /** `null` is Inconclusive: the API sends a completed triage without a suggested status. */
      suggestedStatus: Extract<
        TriageSuggestedStatusResponse,
        'true_positive' | 'suspicious' | 'false_positive' | 'wont_fix'
      > | null
    }
  | { status: 'failed' | 'no_recommendations' | 'blocked' | 'excluded' | 'cancelled' | 'in_progress' }

/** The representative fix result. Only a completed fix has changesets, a confidence and patches. */
export type MockFix =
  | {
      status: 'completed'
      confidence: Confidence
      /** `merged` is also `issued`: a merged pull request was issued first. */
      patch: 'issued' | 'merged' | null
    }
  | { status: 'failed' | 'no_recommendations' | 'blocked' | 'excluded' | 'cancelled' | 'in_progress' }

/** The representative SCA result, from an SCA-scoped analysis, which never runs fix. */
export type MockSca =
  | { status: 'completed'; classification: 'exploitable' | 'not_exploitable' | 'inconclusive' }
  | { status: 'failed' | 'blocked' | 'excluded' | 'in_progress' }

export type MockFinding = {
  id: string
  title: string
  rule: string
  /**
   * `rank` is on the same 0-10 scale as the score query parameters, not the platform's
   * `round(score * 10)`. See `isRankWithinScoreRange` before changing these values.
   */
  severity: { label: string; rank: number }
  /**
   * Contextual CVSS for the findings-grid `CVSS` column. `baseCvss` is the advisory score;
   * `effectiveCvss` is the contextual (Pixee-adjusted) score the column shows — equal to base
   * when unchanged, lower when the environment reduced it. Omit both for findings with no CVSS
   * (the column renders `-`). Same `rank` convention as `severity` above.
   */
  effectiveCvss?: Severity
  baseCvss?: Severity
  /**
   * The completed triage's suggested severity, so SAST findings carry one only with a completed
   * triage. SCA fixtures set it for the contextual-severity columns. Same `rank` convention as
   * `severity` above — read that note before changing these values.
   */
  suggestedSeverity: { label: string; rank: number } | null
  triage: MockTriage | null
  fix: MockFix | null
  sca: MockSca | null
  /** Only meaningful with a completed triage, which is what the API's filter requires. */
  severityUpdate: SeverityUpdateFilter | null
}

// ---------------------------------------------------------------------------
// Derived state — mirrors pixee-platform's FindingSpecification and QueriesImpl
// ---------------------------------------------------------------------------

const isTerminal = (result: { status: string } | null): boolean => result !== null && result.status !== 'in_progress'

const isCompletedTriage = (
  finding: MockFinding
): finding is MockFinding & { triage: Extract<MockTriage, { status: 'completed' }> } =>
  finding.triage?.status === 'completed'

const isCompletedFix = (
  finding: MockFinding
): finding is MockFinding & { fix: Extract<MockFix, { status: 'completed' }> } => finding.fix?.status === 'completed'

/**
 * The `analyzed=true` filter: a terminal triage and either a terminal fix or a triage that was not
 * successful (fix never runs then), or for SCA a terminal SCA result, whose analysis runs no fix.
 */
export const isAnalyzed = (finding: MockFinding): boolean => {
  const isSastAnalyzed =
    isTerminal(finding.triage) && (isTerminal(finding.fix) || finding.triage?.status !== 'completed')
  return isSastAnalyzed || isTerminal(finding.sca)
}

/**
 * The `completed_analysis` statistic. Deliberately not `isAnalyzed`: the API counts a completed
 * fix, a completed, blocked or excluded triage, or a completed SCA result, so a failed triage is
 * analyzed but not counted here, and a triaged finding waiting on its fix is counted but not
 * analyzed.
 */
export const hasCompletedAnalysis = (finding: MockFinding): boolean =>
  finding.fix?.status === 'completed' ||
  finding.triage?.status === 'completed' ||
  finding.triage?.status === 'blocked' ||
  finding.triage?.status === 'excluded' ||
  finding.sca?.status === 'completed'

/** The `in_progress_analysis` statistic: some stage has a result without an outcome. */
export const isAnalysisInProgress = (finding: MockFinding): boolean =>
  finding.triage?.status === 'in_progress' ||
  finding.fix?.status === 'in_progress' ||
  finding.sca?.status === 'in_progress'

/** `composite.ready_to_fix`: a completed fix on a true positive. Likely true positives do not count. */
export const isReadyToFix = (finding: MockFinding): boolean =>
  isCompletedFix(finding) && isCompletedTriage(finding) && finding.triage.suggestedStatus === 'true_positive'

/** `composite.no_fix_necessary`: a completed fix on a false positive. */
export const isNoFixNecessary = (finding: MockFinding): boolean =>
  isCompletedFix(finding) && isCompletedTriage(finding) && finding.triage.suggestedStatus === 'false_positive'

// ---------------------------------------------------------------------------
// Static finding lists
// ---------------------------------------------------------------------------

export const SAST_FINDINGS: readonly MockFinding[] = [
  // ── true positive (8): fixes ready, issued or merged; sast-7's fix is blocked, so it needs attention
  {
    id: 'sast-1',
    title: 'SQL Injection in UserController',
    rule: 'java:S3649',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: { label: 'critical', rank: 9.0 },
    triage: { status: 'completed', suggestedStatus: 'true_positive' },
    fix: { status: 'completed', confidence: 'high', patch: 'merged' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-2',
    title: 'Hardcoded credentials in config',
    rule: 'java:S2068',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'critical', rank: 9.0 },
    triage: { status: 'completed', suggestedStatus: 'true_positive' },
    fix: { status: 'completed', confidence: 'high', patch: 'issued' },
    sca: null,
    severityUpdate: 'increased',
  },
  {
    id: 'sast-3',
    title: 'XSS in template rendering',
    rule: 'java:S5131',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'high', rank: 8.0 },
    triage: { status: 'completed', suggestedStatus: 'true_positive' },
    fix: { status: 'completed', confidence: 'medium', patch: 'issued' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-4',
    title: 'Command injection in ProcessBuilder',
    rule: 'java:S2076',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: { label: 'critical', rank: 9.0 },
    triage: { status: 'completed', suggestedStatus: 'true_positive' },
    fix: { status: 'completed', confidence: 'high', patch: 'merged' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-5',
    title: 'Insecure cookie without HttpOnly flag',
    rule: 'java:S3330',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triage: { status: 'completed', suggestedStatus: 'true_positive' },
    fix: { status: 'completed', confidence: 'medium', patch: null },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-6',
    title: 'XML external entity injection',
    rule: 'java:S2755',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'critical', rank: 9.0 },
    triage: { status: 'completed', suggestedStatus: 'true_positive' },
    fix: { status: 'completed', confidence: 'low', patch: null },
    sca: null,
    severityUpdate: 'increased',
  },
  {
    id: 'sast-7',
    title: 'Unencrypted socket connection',
    rule: 'java:S5332',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'high', rank: 8.0 },
    triage: { status: 'completed', suggestedStatus: 'true_positive' },
    fix: { status: 'blocked' },
    sca: null,
    severityUpdate: 'increased',
  },
  {
    id: 'sast-8',
    title: 'Insecure random number generator',
    rule: 'java:S2245',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triage: { status: 'completed', suggestedStatus: 'true_positive' },
    fix: { status: 'completed', confidence: 'high', patch: 'issued' },
    sca: null,
    severityUpdate: 'no_update',
  },
  // ── likely true positive (6): sast-10's fix is blocked and sast-13's excluded
  {
    id: 'sast-9',
    title: 'Insecure deserialization detected',
    rule: 'java:S5135',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: { label: 'high', rank: 8.0 },
    triage: { status: 'completed', suggestedStatus: 'suspicious' },
    fix: { status: 'completed', confidence: 'medium', patch: null },
    sca: null,
    severityUpdate: 'decreased',
  },
  {
    id: 'sast-10',
    title: 'LDAP injection vulnerability',
    rule: 'java:S2078',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'high', rank: 8.0 },
    triage: { status: 'completed', suggestedStatus: 'suspicious' },
    fix: { status: 'blocked' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-11',
    title: 'Expression language injection',
    rule: 'java:S5334',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'high', rank: 8.0 },
    triage: { status: 'completed', suggestedStatus: 'suspicious' },
    fix: { status: 'completed', confidence: 'medium', patch: 'issued' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-12',
    title: 'Regex denial of service',
    rule: 'java:S2631',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triage: { status: 'completed', suggestedStatus: 'suspicious' },
    fix: { status: 'completed', confidence: 'high', patch: null },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-13',
    title: 'Unsafe reflection usage',
    rule: 'java:S3011',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'low', rank: 4.0 },
    triage: { status: 'completed', suggestedStatus: 'suspicious' },
    fix: { status: 'excluded' },
    sca: null,
    severityUpdate: 'decreased',
  },
  {
    id: 'sast-14',
    title: 'Server-side template injection',
    rule: 'java:S6396',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: { label: 'critical', rank: 9.0 },
    triage: { status: 'completed', suggestedStatus: 'suspicious' },
    fix: { status: 'completed', confidence: 'high', patch: 'merged' },
    sca: null,
    severityUpdate: 'no_update',
  },
  // ── inconclusive (4): triage completed without a suggested status
  {
    id: 'sast-15',
    title: 'Potential SSRF in URL fetch',
    rule: 'java:S5144',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'low', rank: 4.0 },
    triage: { status: 'completed', suggestedStatus: null },
    fix: { status: 'completed', confidence: 'no-rating', patch: null },
    sca: null,
    severityUpdate: 'decreased',
  },
  {
    id: 'sast-16',
    title: 'Possible information leak via error message',
    rule: 'java:S5164',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triage: { status: 'completed', suggestedStatus: null },
    fix: { status: 'no_recommendations' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-17',
    title: 'Unchecked type cast',
    rule: 'java:S1948',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triage: { status: 'completed', suggestedStatus: null },
    fix: { status: 'blocked' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-18',
    title: 'Ambiguous cryptographic usage',
    rule: 'java:S5547',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triage: { status: 'completed', suggestedStatus: null },
    fix: { status: 'completed', confidence: 'low', patch: null },
    sca: null,
    severityUpdate: 'decreased',
  },
  // ── false positive (5): sast-19 and sast-20 carry a fix anyway (no fix necessary), the rest are excluded
  {
    id: 'sast-19',
    title: 'Unused private method detected',
    rule: 'java:S1144',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triage: { status: 'completed', suggestedStatus: 'false_positive' },
    fix: { status: 'completed', confidence: 'medium', patch: null },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-20',
    title: 'Potential null pointer dereference',
    rule: 'java:S2259',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triage: { status: 'completed', suggestedStatus: 'false_positive' },
    fix: { status: 'completed', confidence: 'low', patch: null },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-21',
    title: 'Redundant null check after instanceof',
    rule: 'java:S1872',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triage: { status: 'completed', suggestedStatus: 'false_positive' },
    fix: { status: 'excluded' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-22',
    title: 'Dead code in exception handler',
    rule: 'java:S1763',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triage: { status: 'completed', suggestedStatus: 'false_positive' },
    fix: { status: 'excluded' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-23',
    title: 'Flagged logging of sensitive data',
    rule: 'java:S5145',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'low', rank: 4.0 },
    triage: { status: 'completed', suggestedStatus: 'false_positive' },
    fix: { status: 'excluded' },
    sca: null,
    severityUpdate: 'decreased',
  },
  // ── won't fix (3): fix excluded
  {
    id: 'sast-24',
    title: 'Weak cryptographic algorithm',
    rule: 'java:S5542',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triage: { status: 'completed', suggestedStatus: 'wont_fix' },
    fix: { status: 'excluded' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-25',
    title: 'Deprecated API usage in legacy module',
    rule: 'java:S1874',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triage: { status: 'completed', suggestedStatus: 'wont_fix' },
    fix: { status: 'excluded' },
    sca: null,
    severityUpdate: 'no_update',
  },
  {
    id: 'sast-26',
    title: 'Hardcoded IP address in config',
    rule: 'java:S1313',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triage: { status: 'completed', suggestedStatus: 'wont_fix' },
    fix: { status: 'excluded' },
    sca: null,
    severityUpdate: 'no_update',
  },
  // ── triage had no recommendations (2): fix never runs after a non-successful triage
  {
    id: 'sast-27',
    title: 'Information exposure through error',
    rule: 'java:S5164',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: null,
    triage: { status: 'no_recommendations' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-28',
    title: 'Missing input validation',
    rule: 'java:S5145',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: null,
    triage: { status: 'no_recommendations' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  // ── analysis in progress (7): triage running on six; sast-44 is triaged and waiting on its fix
  {
    id: 'sast-29',
    title: 'Potential HTTP response splitting',
    rule: 'java:S5167',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: null,
    triage: { status: 'in_progress' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-30',
    title: 'Cleartext storage of sensitive data',
    rule: 'java:S5443',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: null,
    triage: { status: 'in_progress' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-31',
    title: 'Insufficient session expiration',
    rule: 'java:S3415',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: null,
    triage: { status: 'in_progress' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-41',
    title: 'Server-side request forgery in webhook handler',
    rule: 'java:S5144',
    severity: { label: 'critical', rank: 9.5 },
    suggestedSeverity: null,
    triage: { status: 'in_progress' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-42',
    title: 'Weak hashing algorithm for stored credentials',
    rule: 'java:S4790',
    severity: { label: 'high', rank: 7.5 },
    suggestedSeverity: null,
    triage: { status: 'in_progress' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-43',
    title: 'Unvalidated redirect on login callback',
    rule: 'java:S5146',
    severity: { label: 'medium', rank: 5.5 },
    suggestedSeverity: null,
    triage: { status: 'in_progress' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-44',
    title: 'Missing rate limit on password reset',
    rule: 'java:S5693',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triage: { status: 'completed', suggestedStatus: 'true_positive' },
    fix: { status: 'in_progress' },
    sca: null,
    severityUpdate: 'no_update',
  },
  // ── triage failed (2)
  {
    id: 'sast-32',
    title: 'Unsafe YAML parsing',
    rule: 'java:S5361',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: null,
    triage: { status: 'failed' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-33',
    title: 'Improper certificate validation',
    rule: 'java:S4830',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: null,
    triage: { status: 'failed' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  // ── triage excluded and blocked (2)
  {
    id: 'sast-34',
    title: 'Overly permissive file permissions',
    rule: 'java:S2612',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: null,
    triage: { status: 'excluded' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-35',
    title: 'Unvalidated dynamic method invocation',
    rule: 'java:S3878',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: null,
    triage: { status: 'blocked' },
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  // ── not analyzed yet (5): no results at all
  {
    id: 'sast-36',
    title: 'Path traversal in file upload',
    rule: 'java:S2083',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: null,
    triage: null,
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-37',
    title: 'Race condition in file access',
    rule: 'java:S3064',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: null,
    triage: null,
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-38',
    title: 'Unvalidated redirect target',
    rule: 'java:S5147',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: null,
    triage: null,
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-39',
    title: 'Weak password hashing algorithm',
    rule: 'java:S5344',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: null,
    triage: null,
    fix: null,
    sca: null,
    severityUpdate: null,
  },
  {
    id: 'sast-40',
    title: 'Missing CSRF token validation',
    rule: 'java:S4502',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: null,
    triage: null,
    fix: null,
    sca: null,
    severityUpdate: null,
  },
]

export const SCA_FINDINGS: readonly MockFinding[] = [
  // ── exploitable (2)
  {
    id: 'sca-1',
    title: 'CVE-2023-1234 in log4j',
    rule: 'sca:CVE-2023-1234',
    severity: { label: 'critical', rank: 9.8 },
    // Environment-reduced: advisory 9.8, contextual 3.1 (accented + tooltip).
    baseCvss: { label: '9.8', rank: 9.8 },
    effectiveCvss: { label: '3.1', rank: 3.1 },
    suggestedSeverity: { label: 'critical', rank: 5 },
    triage: null,
    fix: null,
    sca: { status: 'completed', classification: 'exploitable' },
    severityUpdate: null,
  },
  {
    id: 'sca-2',
    title: 'CVE-2023-5678 in commons-text',
    rule: 'sca:CVE-2023-5678',
    severity: { label: 'high', rank: 7.5 },
    // Unchanged: contextual equals base (normal color, no tooltip).
    baseCvss: { label: '7.5', rank: 7.5 },
    effectiveCvss: { label: '7.5', rank: 7.5 },
    suggestedSeverity: { label: 'high', rank: 4 },
    triage: null,
    fix: null,
    sca: { status: 'completed', classification: 'exploitable' },
    severityUpdate: null,
  },
  // ── not exploitable (2)
  {
    id: 'sca-3',
    title: 'CVE-2023-9012 in guava',
    rule: 'sca:CVE-2023-9012',
    severity: { label: 'medium', rank: 5.3 },
    // Not exploitable in this codebase: contextual reduced to 0 (accented + tooltip).
    baseCvss: { label: '5.3', rank: 5.3 },
    effectiveCvss: { label: '0.0', rank: 0 },
    suggestedSeverity: { label: 'medium', rank: 3 },
    triage: null,
    fix: null,
    sca: { status: 'completed', classification: 'not_exploitable' },
    severityUpdate: null,
  },
  {
    id: 'sca-4',
    title: 'CVE-2023-3456 in jackson-databind',
    rule: 'sca:CVE-2023-3456',
    severity: { label: 'low', rank: 2.1 },
    suggestedSeverity: { label: 'low', rank: 2 },
    triage: null,
    fix: null,
    sca: { status: 'completed', classification: 'not_exploitable' },
    severityUpdate: null,
  },
  // ── inconclusive (1)
  {
    id: 'sca-5',
    title: 'CVE-2023-7890 in spring-core',
    rule: 'sca:CVE-2023-7890',
    severity: { label: 'medium', rank: 4.7 },
    suggestedSeverity: { label: 'high', rank: 4 },
    triage: null,
    fix: null,
    sca: { status: 'completed', classification: 'inconclusive' },
    severityUpdate: null,
  },
  // ── not analyzed yet (1)
  {
    id: 'sca-6',
    title: 'CVE-2023-2345 in netty',
    rule: 'sca:CVE-2023-2345',
    severity: { label: 'high', rank: 8.1 },
    suggestedSeverity: null,
    triage: null,
    fix: null,
    sca: null,
    severityUpdate: null,
  },
]

// ---------------------------------------------------------------------------
// Filter predicates — each mirrors the matching FindingSpecification filter
// ---------------------------------------------------------------------------

export type FindingPredicate = (finding: MockFinding) => boolean

/**
 * The API's suggested statuses other than Not triaged, Blocked, Excluded and Error only match a
 * completed triage. Not triaged matches anything else, including no triage at all, so it overlaps
 * Blocked, Excluded and Error — as it does on the platform.
 */
const matchesTriageSuggestedStatus = (finding: MockFinding, status: string): boolean =>
  match(status)
    .with('true_positive', 'suspicious', 'false_positive', 'wont_fix', value =>
      isCompletedTriage(finding) ? finding.triage.suggestedStatus === value : false
    )
    .with('inconclusive', () => (isCompletedTriage(finding) ? finding.triage.suggestedStatus === null : false))
    .with('not_triaged', () => finding.triage === null || finding.triage.status !== 'completed')
    .with('blocked', () => finding.triage?.status === 'blocked')
    .with('excluded', () => finding.triage?.status === 'excluded')
    .with('error', () => finding.triage?.status === 'failed')
    .otherwise(() => false)

export const byTriageSuggestedStatus =
  (statuses: string[]): FindingPredicate =>
  finding =>
    statuses.some(status => matchesTriageSuggestedStatus(finding, status))

const TRIAGE_STATUS_PARAM_TO_STATUS: Record<string, MockTriage['status']> = {
  completed: 'completed',
  failed: 'failed',
  'no-recommendations-available': 'no_recommendations',
}

/** `completed` means a completed triage only — Blocked and Excluded are not, unlike the statistic. */
export const byTriageStatus =
  (statuses: string[]): FindingPredicate =>
  finding =>
    finding.triage !== null && statuses.some(status => TRIAGE_STATUS_PARAM_TO_STATUS[status] === finding.triage?.status)

const FIX_STATUS_PARAM_TO_STATUS: Record<string, MockFix['status']> = {
  completed: 'completed',
  failed: 'failed',
  'no-recommendations-available': 'no_recommendations',
  blocked: 'blocked',
  excluded: 'excluded',
}

/** Matches the representative fix's single outcome; a fix still running matches none. */
export const byFixStatus =
  (statuses: string[]): FindingPredicate =>
  finding =>
    finding.fix !== null && statuses.some(status => FIX_STATUS_PARAM_TO_STATUS[status] === finding.fix?.status)

export const byFixConfidence =
  (confidences: string[]): FindingPredicate =>
  finding =>
    isCompletedFix(finding) && confidences.includes(finding.fix.confidence)

const SCA_CLASSIFICATION_PARAM_TO_FIELD: Record<string, Extract<MockSca, { status: 'completed' }>['classification']> = {
  exploitable: 'exploitable',
  'not-exploitable': 'not_exploitable',
  inconclusive: 'inconclusive',
  'potentially-exploitable': 'inconclusive',
}

export const byScaClassification =
  (classifications: string[]): FindingPredicate =>
  finding => {
    const { sca } = finding
    return (
      sca?.status === 'completed' &&
      classifications.some(param => SCA_CLASSIFICATION_PARAM_TO_FIELD[param] === sca.classification)
    )
  }

/** `not-analyzed` matches a finding whose SCA result has no outcome, or that has none at all. */
export const byScaStatus =
  (statuses: string[]): FindingPredicate =>
  finding =>
    statuses.some(status =>
      status === 'not-analyzed' ? !isTerminal(finding.sca) : finding.sca !== null && finding.sca.status === status
    )

export const byAnalyzed =
  (analyzed: boolean): FindingPredicate =>
  finding =>
    isAnalyzed(finding) === analyzed

export const bySeverityLabel =
  (labels: string[]): FindingPredicate =>
  f => {
    const lower = labels.map(l => l.toLowerCase())
    return lower.includes(f.severity.label.toLowerCase())
  }

/** The platform reads the suggested severity off a completed triage, so nothing else matches. */
export const bySuggestedSeverityLabel =
  (labels: string[]): FindingPredicate =>
  f => {
    const lower = labels.map(l => l.toLowerCase())
    return (
      isCompletedTriage(f) && f.suggestedSeverity !== null && lower.includes(f.suggestedSeverity.label.toLowerCase())
    )
  }

/**
 * Compares a mock severity rank against the inclusive bounds of a score-range query parameter.
 *
 * Mock ranks share the 0-10 scale of the `minimum/maximum-severity-score` parameters, so the bounds
 * are compared against them directly. The platform does not: it persists rank as
 * `round(score * 10)` and converts the bounds server-side, so a real rank of `89` answers a bound of
 * `8.9`. Both range predicates go through here so that difference is stated once rather than
 * duplicated, and `mock-findings.test.ts` pins the mock convention — if the fixtures above are ever
 * restated in platform-shaped ranks, that test fails instead of every score filter silently
 * matching nothing.
 */
const isRankWithinScoreRange = (rank: number, min: number | null, max: number | null): boolean => {
  if (min !== null && rank < min) return false
  if (max !== null && rank > max) return false
  return true
}

export const bySeverityScoreRange =
  (min: number | null, max: number | null): FindingPredicate =>
  f =>
    isRankWithinScoreRange(f.severity.rank, min, max)

export const bySuggestedSeverityScoreRange =
  (min: number | null, max: number | null): FindingPredicate =>
  f =>
    // Only a completed triage has a suggested severity to compare, mirroring the platform's
    // outcome-type guard on the same filter.
    isCompletedTriage(f) && f.suggestedSeverity !== null && isRankWithinScoreRange(f.suggestedSeverity.rank, min, max)

export const bySeverityUpdate =
  (updates: string[]): FindingPredicate =>
  f =>
    isCompletedTriage(f) && f.severityUpdate !== null && updates.includes(f.severityUpdate)

/** `issued` matches any patch, merged or not; `merged` only a merged one. Patches need a completed fix. */
export const byPatchStatus =
  (statuses: string[]): FindingPredicate =>
  finding => {
    if (!isCompletedFix(finding) || finding.fix.patch === null) {
      return false
    }
    const { patch } = finding.fix
    return statuses.some(status => (status === 'issued' ? true : status === patch))
  }

// ---------------------------------------------------------------------------
// Compose
// ---------------------------------------------------------------------------

export const composePredicates = (predicates: FindingPredicate[]): FindingPredicate =>
  predicates.length === 0 ? () => true : finding => predicates.every(p => p(finding))

export const buildFilterFromParams = (params: URLSearchParams): FindingPredicate => {
  const predicates: FindingPredicate[] = []

  const triageSuggested = params.getAll(SCAN_FINDINGS_PARAMS.triageSuggestedStatus)
  if (triageSuggested.length > 0) predicates.push(byTriageSuggestedStatus(triageSuggested))

  const triageStatus = params.getAll(SCAN_FINDINGS_PARAMS.triageStatus)
  if (triageStatus.length > 0) predicates.push(byTriageStatus(triageStatus))

  const fixStatus = params.getAll(SCAN_FINDINGS_PARAMS.fixStatus)
  if (fixStatus.length > 0) predicates.push(byFixStatus(fixStatus))

  const fixConfidences = params.getAll(SCAN_FINDINGS_PARAMS.fixConfidences)
  if (fixConfidences.length > 0) predicates.push(byFixConfidence(fixConfidences))

  const scaClassifications = params.getAll(SCAN_FINDINGS_PARAMS.scaClassifications)
  if (scaClassifications.length > 0) predicates.push(byScaClassification(scaClassifications))

  const scaStatusParams = params.getAll(SCAN_FINDINGS_PARAMS.scaStatus)
  if (scaStatusParams.length > 0) predicates.push(byScaStatus(scaStatusParams))

  const analyzedParam = params.get(SCAN_FINDINGS_PARAMS.analyzed)
  if (analyzedParam !== null) predicates.push(byAnalyzed(analyzedParam === 'true'))

  const severityLabels = params.getAll(SCAN_FINDINGS_PARAMS.includeSeverityLabel)
  if (severityLabels.length > 0) predicates.push(bySeverityLabel(severityLabels))

  const suggestedSeverityLabels = params.getAll(SCAN_FINDINGS_PARAMS.includeSuggestedSeverityLabel)
  if (suggestedSeverityLabels.length > 0) predicates.push(bySuggestedSeverityLabel(suggestedSeverityLabels))

  const minScore = params.get(SCAN_FINDINGS_PARAMS.minimumSeverityScore)
  const maxScore = params.get(SCAN_FINDINGS_PARAMS.maximumSeverityScore)
  if (minScore !== null || maxScore !== null) {
    predicates.push(bySeverityScoreRange(minScore ? Number(minScore) : null, maxScore ? Number(maxScore) : null))
  }

  const minSuggestedScore = params.get(SCAN_FINDINGS_PARAMS.minimumSuggestedSeverityScore)
  const maxSuggestedScore = params.get(SCAN_FINDINGS_PARAMS.maximumSuggestedSeverityScore)
  if (minSuggestedScore !== null || maxSuggestedScore !== null) {
    predicates.push(
      bySuggestedSeverityScoreRange(
        minSuggestedScore ? Number(minSuggestedScore) : null,
        maxSuggestedScore ? Number(maxSuggestedScore) : null
      )
    )
  }

  const severityUpdates = params.getAll(SCAN_FINDINGS_PARAMS.severityUpdates)
  if (severityUpdates.length > 0) predicates.push(bySeverityUpdate(severityUpdates))

  const patchStatuses = params.getAll(SCAN_FINDINGS_PARAMS.patchStatus)
  if (patchStatuses.length > 0) predicates.push(byPatchStatus(patchStatuses))

  return composePredicates(predicates)
}

// ---------------------------------------------------------------------------
// Aggregate stats reducer
// ---------------------------------------------------------------------------

export type AggregateStats = FindingAggregateResponseStatistics

export const emptyStats = (): AggregateStats => ({
  total: 0,
  completed_analysis: 0,
  in_progress_analysis: 0,
  triage: {
    status: { completed: 0, failed: 0, no_recommendations: 0, in_progress: 0 },
    outcome: {
      true_positive: 0,
      likely_true_positive: 0,
      inconclusive: 0,
      false_positive: 0,
      wont_fix: 0,
      blocked: 0,
      excluded: 0,
      not_triaged: 0,
    },
  },
  fix: {
    status: { completed: 0, failed: 0, no_recommendations: 0, in_progress: 0 },
    outcome: { has_fix: 0, blocked: 0, excluded: 0 },
    confidence: { high: 0, medium: 0, low: 0, no_rating: 0 },
  },
  sca: {
    status: { completed: 0, failed: 0, blocked: 0, excluded: 0, in_progress: 0 },
    classification: { exploitable: 0, not_exploitable: 0, inconclusive: 0 },
  },
  composite: { ready_to_fix: 0, no_fix_necessary: 0 },
})

/**
 * Adds one finding to the aggregate statistics the way `QueriesImpl.getStatistics` counts it, so a
 * filtered page and its statistics always describe the same findings.
 */
export const accumulateFinding = (stats: AggregateStats, finding: MockFinding): AggregateStats => {
  const { triage, fix, sca } = finding
  stats.total++

  if (hasCompletedAnalysis(finding)) stats.completed_analysis++
  if (isAnalysisInProgress(finding)) stats.in_progress_analysis++

  if (triage) {
    // The statistic's `completed` counts Blocked and Excluded too, unlike the `triage-status` filter.
    match(triage.status)
      .with('completed', 'blocked', 'excluded', () => stats.triage.status.completed++)
      .with('failed', () => stats.triage.status.failed++)
      .with('no_recommendations', () => stats.triage.status.no_recommendations++)
      .with('in_progress', () => stats.triage.status.in_progress++)
      // A cancelled triage is terminal but has no bucket of its own in the statistics.
      .with('cancelled', () => undefined)
      .exhaustive()

    if (triage.status === 'completed') {
      match(triage.suggestedStatus)
        .with('true_positive', () => stats.triage.outcome.true_positive++)
        .with('suspicious', () => stats.triage.outcome.likely_true_positive++)
        .with('false_positive', () => stats.triage.outcome.false_positive++)
        .with('wont_fix', () => stats.triage.outcome.wont_fix++)
        .with(null, () => stats.triage.outcome.inconclusive++)
        .exhaustive()
    }
    if (triage.status === 'blocked') stats.triage.outcome.blocked++
    if (triage.status === 'excluded') stats.triage.outcome.excluded++
    // `not_triaged` stays 0: the platform returns a literal 0 for it.
  }

  if (fix) {
    match(fix)
      .with({ status: 'completed' }, ({ confidence }) => {
        stats.fix.status.completed++
        // Every mock completed fix has changesets, so it is also a fix outcome.
        stats.fix.outcome.has_fix++
        stats.fix.confidence[confidence === 'no-rating' ? 'no_rating' : confidence]++
      })
      .with({ status: 'failed' }, () => stats.fix.status.failed++)
      .with({ status: 'no_recommendations' }, () => stats.fix.status.no_recommendations++)
      .with({ status: 'in_progress' }, () => stats.fix.status.in_progress++)
      .with({ status: 'blocked' }, () => stats.fix.outcome.blocked++)
      .with({ status: 'excluded' }, () => stats.fix.outcome.excluded++)
      .with({ status: 'cancelled' }, () => undefined)
      .exhaustive()
  }

  if (sca) {
    match(sca)
      .with({ status: 'completed' }, ({ classification }) => {
        stats.sca.status.completed++
        stats.sca.classification[classification]++
      })
      .with({ status: 'failed' }, () => stats.sca.status.failed++)
      .with({ status: 'blocked' }, () => stats.sca.status.blocked++)
      .with({ status: 'excluded' }, () => stats.sca.status.excluded++)
      .with({ status: 'in_progress' }, () => stats.sca.status.in_progress++)
      .exhaustive()
  }

  if (isReadyToFix(finding)) stats.composite.ready_to_fix++
  if (isNoFixNecessary(finding)) stats.composite.no_fix_necessary++

  return stats
}

export const filterAndAccumulate = (
  findings: readonly MockFinding[],
  predicate: FindingPredicate
): { items: MockFinding[]; stats: AggregateStats } =>
  findings.reduce<{ items: MockFinding[]; stats: AggregateStats }>(
    (acc, finding) => {
      if (predicate(finding)) {
        acc.items.push(finding)
        accumulateFinding(acc.stats, finding)
      }
      return acc
    },
    { items: [], stats: emptyStats() }
  )

// ---------------------------------------------------------------------------
// fromFindingResponse — converts FindingResponse → MockFinding
// ---------------------------------------------------------------------------

type ResultOutcome = { type: string } | undefined

const toMockTriage = (result: FindingResultResponse | undefined): MockTriage | null => {
  if (!result) return null
  const outcome = result.outcome as ResultOutcome
  if (!outcome) return { status: 'in_progress' }
  if (result.outcome?.type === 'completed-triage') {
    const status = result.outcome.suggested_status
    return {
      status: 'completed',
      suggestedStatus:
        status === 'true_positive' || status === 'suspicious' || status === 'false_positive' || status === 'wont_fix'
          ? status
          : null,
    }
  }
  return match(outcome.type)
    .returnType<MockTriage>()
    .with('failed-triage', () => ({ status: 'failed' }))
    .with('no-recommendations-triage', () => ({ status: 'no_recommendations' }))
    .with('blocked-triage', () => ({ status: 'blocked' }))
    .with('excluded-triage', () => ({ status: 'excluded' }))
    .otherwise(() => ({ status: 'cancelled' }))
}

const toMockFix = (result: FindingResultResponse | undefined): MockFix | null => {
  if (!result) return null
  const outcome = result.outcome as ResultOutcome
  if (!outcome) return { status: 'in_progress' }
  if (result.outcome?.type === 'completed-fix') {
    return { status: 'completed', confidence: result.outcome.confidence ?? 'no-rating', patch: null }
  }
  return match(outcome.type)
    .returnType<MockFix>()
    .with('failed-fix', () => ({ status: 'failed' }))
    .with('no-recommendations-fix', () => ({ status: 'no_recommendations' }))
    .with('blocked-fix', () => ({ status: 'blocked' }))
    .with('excluded-fix', 'out-of-scope-fix', () => ({ status: 'excluded' }))
    .otherwise(() => ({ status: 'cancelled' }))
}

const toMockSca = (result: FindingResultResponse | undefined): MockSca | null => {
  if (!result) return null
  const outcome = result.outcome as ResultOutcome
  if (!outcome) return { status: 'in_progress' }
  if (result.outcome?.type === 'completed-sca') {
    const classification = SCA_CLASSIFICATION_PARAM_TO_FIELD[result.outcome.classification]
    return classification ? { status: 'completed', classification } : { status: 'failed' }
  }
  return match(outcome.type)
    .returnType<MockSca>()
    .with('blocked-sca', () => ({ status: 'blocked' }))
    .with('excluded-sca', () => ({ status: 'excluded' }))
    .otherwise(() => ({ status: 'failed' }))
}

export const fromFindingResponse = (finding: FindingResponse): MockFinding => {
  const results = finding._embedded?.['representative-results']?._embedded?.items ?? []
  const triageResult = results.find(r => r.type === 'triage')

  const suggestedSeverity: MockFinding['suggestedSeverity'] =
    triageResult?.outcome?.type === 'completed-triage' && triageResult.outcome.suggested_severity
      ? {
          label: triageResult.outcome.suggested_severity.label ?? 'unknown',
          rank: triageResult.outcome.suggested_severity.rank,
        }
      : null

  return {
    id: finding.id,
    title: finding.title ?? '',
    rule: finding.rule,
    severity: {
      label: finding.severity?.label ?? 'unknown',
      rank: finding.severity?.rank ?? 0,
    },
    suggestedSeverity,
    triage: toMockTriage(triageResult),
    fix: toMockFix(results.find(r => r.type === 'fix')),
    sca: toMockSca(results.find(r => r.type === 'sca')),
    severityUpdate: null,
    effectiveCvss: finding.effective_cvss,
    baseCvss: finding.base_cvss,
  }
}

// ---------------------------------------------------------------------------
// toFindingResponse — converts MockFinding to FindingResponse
// ---------------------------------------------------------------------------

/** The outcome a result of the given type carries. */
type OutcomeOf<TType extends FindingResultResponse['type']> = Extract<FindingResultResponse, { type: TType }>['outcome']

const resultLinks = { self: { href: `#` }, finding: { href: `#` }, analysis: { href: '#' } }
const fixResultLinks = {
  ...resultLinks,
  changesets: { href: '#' },
  patches: { href: '#' },
  'latest-patch': { href: '#' },
}

/** A result still running has no outcome and no completion time: that absence is what the UI reads as in progress. */
const buildTriageResult = (finding: MockFinding, findingId: string): FindingResultResponse | null => {
  const { triage } = finding
  if (!triage) return null
  const base = { id: `triage-${findingId}`, type: 'triage' as const, _links: resultLinks }
  const outcome = match<typeof triage, OutcomeOf<'triage'>>(triage)
    .with({ status: 'in_progress' }, () => undefined)
    .with({ status: 'completed' }, ({ suggestedStatus }) => ({
      type: 'completed-triage' as const,
      suggested_status: suggestedStatus ?? undefined,
      suggested_severity: finding.suggestedSeverity ?? { label: 'unknown', rank: 0 },
      summary: `Triage result for ${finding.title}`,
    }))
    .with({ status: 'failed' }, () => ({
      type: 'failed-triage' as const,
      problem: { code: 'TRIAGE_FAILED', message: 'Triage failed' },
    }))
    .with({ status: 'no_recommendations' }, () => ({ type: 'no-recommendations-triage' as const }))
    .with({ status: 'blocked' }, () => ({ type: 'blocked-triage' as const, reason: 'Triage was blocked' }))
    .with({ status: 'excluded' }, () => ({ type: 'excluded-triage' as const, reason: 'Excluded from triage' }))
    .with({ status: 'cancelled' }, () => ({ type: 'cancelled-triage' as const }))
    .exhaustive()

  return outcome ? { ...base, completed: '2024-01-15T10:00:00Z', outcome } : base
}

const buildFixResult = (finding: MockFinding, findingId: string): FindingResultResponse | null => {
  const { fix } = finding
  if (!fix) return null
  const base = { id: `fix-${findingId}`, type: 'fix' as const, _links: fixResultLinks }
  const outcome = match<typeof fix, OutcomeOf<'fix'>>(fix)
    .with({ status: 'in_progress' }, () => undefined)
    .with({ status: 'completed' }, ({ confidence }) => ({
      type: 'completed-fix' as const,
      strategy: 'ai' as const,
      confidence,
    }))
    .with({ status: 'failed' }, () => ({
      type: 'failed-fix' as const,
      problem: { code: 'FIX_FAILED', message: 'Fix failed' },
    }))
    .with({ status: 'no_recommendations' }, () => ({ type: 'no-recommendations-fix' as const }))
    .with({ status: 'blocked' }, () => ({ type: 'blocked-fix' as const, reason: 'Fix was blocked' }))
    .with({ status: 'excluded' }, () => ({ type: 'excluded-fix' as const, reason: 'Excluded from fix' }))
    .with({ status: 'cancelled' }, () => ({ type: 'cancelled-fix' as const }))
    .exhaustive()

  return outcome ? { ...base, completed: '2024-01-15T11:00:00Z', outcome } : base
}

const SCA_FIELD_TO_PARAM: Record<
  Extract<MockSca, { status: 'completed' }>['classification'],
  ScaClassificationResponse
> = {
  exploitable: 'exploitable',
  not_exploitable: 'not-exploitable',
  inconclusive: 'inconclusive',
}

const buildScaResult = (finding: MockFinding, findingId: string): FindingResultResponse | null => {
  const { sca } = finding
  if (!sca) return null
  const base = { id: `sca-${findingId}`, type: 'sca' as const, _links: resultLinks }
  const outcome = match<typeof sca, OutcomeOf<'sca'>>(sca)
    .with({ status: 'in_progress' }, () => undefined)
    .with({ status: 'completed' }, ({ classification }) => ({
      type: 'completed-sca' as const,
      classification: SCA_FIELD_TO_PARAM[classification],
    }))
    .with({ status: 'failed' }, () => ({
      type: 'failed-sca' as const,
      problem: { title: 'SCA Failed', detail: 'SCA analysis failed' },
    }))
    .with({ status: 'blocked' }, () => ({ type: 'blocked-sca' as const }))
    .with({ status: 'excluded' }, () => ({ type: 'excluded-sca' as const }))
    .exhaustive()

  return outcome ? { ...base, completed: '2024-01-15T12:00:00Z', outcome } : base
}

export const toFindingResponse = (finding: MockFinding): FindingResponse => {
  const results: FindingResultResponse[] = []

  const triageResult = buildTriageResult(finding, finding.id)
  if (triageResult) results.push(triageResult)

  const fixResult = buildFixResult(finding, finding.id)
  if (fixResult) results.push(fixResult)

  const scaResult = buildScaResult(finding, finding.id)
  if (scaResult) results.push(scaResult)

  return {
    id: finding.id,
    title: finding.title,
    rule: finding.rule,
    severity: finding.severity,
    effective_cvss: finding.effectiveCvss,
    base_cvss: finding.baseCvss,
    html_url: `https://example.com/findings/${finding.id}`,
    _links: {
      self: { href: `/api/v1/scans/scan-id/findings/${finding.id}` },
      results: { href: `/api/v1/scans/scan-id/findings/${finding.id}/results` },
      'representative-results': {
        href: `/api/v1/scans/scan-id/findings/${finding.id}/results?representative=true`,
      },
    },
    _embedded: {
      'representative-results': {
        _links: {
          first: { href: `/api/v1/scans/scan-id/findings/${finding.id}/results?representative=true` },
          self: { href: `/api/v1/scans/scan-id/findings/${finding.id}/results?representative=true` },
          finding: { href: '#' },
        },
        _embedded: { items: results },
        page: { number: 0, size: 10 },
        total: results.length,
      },
    },
  }
}

// ---------------------------------------------------------------------------
// paginateAndFilter — takes mock findings + URL search params, returns a
// fully typed FindingAggregateResponse with filtered/paginated items and stats.
// ---------------------------------------------------------------------------

export const paginateAndFilter = (
  findings: readonly MockFinding[],
  searchParams: URLSearchParams
): FindingAggregateResponse => {
  const pageSize = Number(searchParams.get('page-size')) || 15
  const pageNumber = Number(searchParams.get('page-number')) || 0

  if (pageSize <= 0) throw new Error('page-size must be greater than 0')
  if (pageNumber < 0) throw new Error('page-number must not be negative')

  const predicate = buildFilterFromParams(searchParams)
  const { items: filteredFindings, stats } = filterAndAccumulate(findings, predicate)

  const startIdx = pageNumber * pageSize
  const paginatedItems = filteredFindings.slice(startIdx, startIdx + pageSize).map(toFindingResponse)

  return {
    _links: {
      first: { href: '/api/v1/scans/scan-id/findings' },
      self: { href: '/api/v1/scans/scan-id/findings' },
    },
    total: stats.total,
    page: { number: pageNumber, size: pageSize },
    _embedded: { items: paginatedItems },
    completed_analysis: stats.completed_analysis,
    in_progress_analysis: stats.in_progress_analysis,
    triage: stats.triage,
    fix: stats.fix,
    sca: stats.sca,
    composite: stats.composite,
  } satisfies FindingAggregateResponse
}
